/**
 * WealthPaths - plan_22 (Money plan theme only).
 *
 * The paths on the Wealth tabs, and the small 🌱 / 📈 switch that swaps a
 * tab's card between its path and its graph. The choice is remembered on
 * this phone and shared by Worth, Funds and Invest.
 *
 *   - WorthPath: round-number net worth milestones, with when you'll reach
 *     the next ones at your money plan's pace.
 *   - FundsPath: your first five funds, with their own emojis and amounts.
 *   - InvestPath: your first five investment accounts, with an
 *     "Edit emojis" link. An account's emoji is saved on it as `emoji`.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import PlanPath from "../common/PlanPath";
import { planFundTint } from "../../utils/plan";
import { formatCurrency, getCurrentCurrency } from "../../utils/formatters";

const VIEW_KEY = 'worthly_wealth_path_or_graph';
const readView = () => {
  try { return window.localStorage.getItem(VIEW_KEY) === 'graph' ? 'graph' : 'path'; } catch (e) { return 'path'; }
};

// Path or graph, the same on every Wealth tab.
export const useWealthView = () => {
  const [view, setView] = useState(readView);
  useEffect(() => {
    const on = () => setView(readView());
    window.addEventListener('worthly-wealth-view', on);
    return () => window.removeEventListener('worthly-wealth-view', on);
  }, []);
  const change = (v) => {
    try { window.localStorage.setItem(VIEW_KEY, v); } catch (e) { /* storage unavailable */ }
    setView(v);
    window.dispatchEvent(new Event('worthly-wealth-view'));
  };
  return [view, change];
};

export const ViewToggle = ({ view, onChange }) => {
  // w2_11: words instead of emojis, so it's clear what each one shows
  const btn = (v, text) => (
    <button
      type="button"
      aria-pressed={view === v}
      onClick={() => onChange(v)}
      style={{
        height: 32, border: 0, borderRadius: 999, padding: '0 12px', fontSize: 12.5, fontWeight: 600,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: view === v ? '#25222B' : '#8E8A99',
        background: view === v ? '#FFFFFF' : 'transparent',
        boxShadow: view === v ? '0 1px 3px rgba(40,30,80,0.10)' : 'none',
      }}
    >{text}</button>
  );
  return (
    <span className="flex flex-shrink-0" style={{ gap: 3, background: '#F4F1FB', borderRadius: 999, padding: 3, alignSelf: 'flex-start' }} data-patch="plan_22">
      {btn('path', 'Path')}
      {btn('graph', 'Graph')}
    </span>
  );
};

// $804, $2.1k, $28.4k, $160k, $1.2M
export const shortMoney = (v) => {
  const s = getCurrentCurrency().symbol || '$';
  const n = Math.round(Number(v) || 0);
  const a = Math.abs(n);
  const sign = n < 0 ? '−' : '';
  const trim = (x) => String(x).replace(/\.0$/, '');
  if (a < 1000) return sign + s + a;
  if (a < 1e6) return sign + s + trim(a < 1e5 ? (a / 1000).toFixed(1) : Math.round(a / 1000)) + 'k';
  return sign + s + trim((a / 1e6).toFixed(1)) + 'M';
};

// dark_05: to the cent when there's room (three or fewer), short when there are more.
const pathMoney = (v, count) => (count <= 3 ? formatCurrency(v) : shortMoney(v));
const shortName = (name) => String(name || '').replace(/\s+fund$/i, '').trim() || String(name || '');

/* ------------------------------------------------------------ Worth */
// plan_23: up in $50k steps from $50k to $300k
const MILESTONES = [1000, 5000, 10000, 25000, 50000, 100000, 150000, 200000, 250000, 300000, 400000, 500000, 750000, 1000000, 2000000, 5000000];
const MILE_EMOJI = { 1000: '\u{1F331}', 5000: '\u{1F33F}', 10000: '\u{1F340}', 25000: '\u{1FAB4}', 50000: '\u{1F337}', 100000: '\u{1F333}', 150000: '\u{1F33B}', 200000: '\u{1F338}', 250000: '\u{1F3E1}', 300000: '\u{1F308}', 400000: '⛵', 500000: '\u{1F3DD}\u{FE0F}', 750000: '✨', 1000000: '\u{1F48E}', 2000000: '\u{1F31F}', 5000000: '\u{1F680}' };
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];

// growth(months) is how much your net worth grows by then, from your money
// plan. Without a plan, the dates are left off.
// w2_04: `note` shows under the path when the dates are an estimate.
export const WorthPath = ({ netWorth = 0, growth = null, note = null }) => {
  const nw = Number(netWorth) || 0;
  const passed = MILESTONES.filter((m) => nw >= m);
  const last = passed.length ? passed[passed.length - 1] : null;
  const ahead = MILESTONES.filter((m) => nw < m).slice(0, last ? 3 : 4);
  const now = new Date();
  const when = (m) => {
    if (!growth) return '';
    for (let k = 1; k <= 600; k++) {
      if (nw + growth(k) >= m) {
        const d = new Date(now.getFullYear(), now.getMonth() + k, 1);
        return k <= 36 ? MON[d.getMonth()] + ' ' + d.getFullYear() : String(d.getFullYear());
      }
    }
    return '';
  };
  const label = (m) => shortMoney(m);
  const steps = [
    ...(last ? [{ emoji: MILE_EMOJI[last], label: label(last), sub: 'Passed', done: true }] : []),
    ...ahead.map((m, i) => ({ emoji: MILE_EMOJI[m], label: label(m), sub: when(m), now: i === 0 })),
  ];
  if (!steps.length) return null;
  const n = steps.length;
  const from = last || 0;
  const to = ahead[0] || from;
  const part = last && to > from ? Math.max(0, Math.min(1, (nw - from) / (to - from))) : 0;
  const path = <PlanPath steps={steps} progress={n > 1 ? part / (n - 1) : 0} />;
  if (!note || !steps.some((st) => st.sub && st.sub !== 'Passed')) return path;
  return (
    <>
      {path}
      <p style={{ margin: '14px 0 0', fontSize: 12, color: '#9A8FBF', textAlign: 'center' }}>{note}</p>
    </>
  );
};

// w2_09: the same milestones as the Worth path, for the widget.
export const worthMilestoneSteps = (netWorth = 0, growth = null) => {
  const nw = Number(netWorth) || 0;
  const passed = MILESTONES.filter((m) => nw >= m);
  const last = passed.length ? passed[passed.length - 1] : null;
  const ahead = MILESTONES.filter((m) => nw < m).slice(0, last ? 3 : 4);
  const now = new Date();
  const when = (m) => {
    if (!growth) return '';
    for (let k = 1; k <= 600; k++) {
      if (nw + growth(k) >= m) {
        const d = new Date(now.getFullYear(), now.getMonth() + k, 1);
        return k <= 36 ? MON[d.getMonth()] + ' ' + d.getFullYear() : String(d.getFullYear());
      }
    }
    return '';
  };
  return [
    ...(last ? [{ emoji: MILE_EMOJI[last], label: shortMoney(last), sub: 'Passed', done: true, now: false }] : []),
    ...ahead.map((m, i) => ({ emoji: MILE_EMOJI[m], label: shortMoney(m), sub: when(m), done: false, now: i === 0 })),
  ];
};

// w2_04: without a money plan, how much your net worth has grown a month,
// on average, over the last 6 months: money into funds and investments
// (less what came out), interest, and debt paid off. 0 if it hasn't grown.
export const recentPace = (transactions = [], today = new Date()) => {
  const from = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate());
  let sum = 0;
  (transactions || []).forEach((t) => {
    if (!t || !t.date) return;
    const d = new Date(t.date);
    if (isNaN(d.getTime()) || d < from || d > today) return;
    const a = Number(t.amount) || 0;
    if (t.is_interest && (t.interest_account_type === 'fund' || t.interest_account_type === 'investment')) { sum += a; return; }
    if (t.account_type === 'fund' || t.account_type === 'investment') {
      if (t.transaction_type === 'deposit') sum += a;
      else if (t.transaction_type === 'withdrawal') sum -= a;
      return;
    }
    if (t.is_debt_payment) sum += a;
  });
  const pace = sum / 6;
  return pace > 0 ? pace : 0;
};

/* ------------------------------------------------------------ Funds */
// items: [{ id, emoji, name, balance }] in your Funds order.
export const FundsPath = ({ items = [] }) => {
  const steps = items.slice(0, 5).map((f, i) => {
    const t = f.tint || planFundTint(i); // w2_04: the fund's own colour
    return { emoji: f.emoji || '\u{1F4B0}', label: shortName(f.name), amount: pathMoney(f.balance, items.length), amountBg: t.wash, amountInk: t.ink };
  });
  if (!steps.length) return null;
  return <PlanPath steps={steps} flow />;
};

/* ------------------------------------------------------------ Invest */
// A starting emoji from the account's name, until you pick one.
export const investEmoji = (inv) => {
  if (inv && inv.emoji) return inv.emoji;
  const n = String((inv && inv.name) || '').toLowerCase();
  if (/rrsp|rrif|retire/.test(n)) return '\u{1F334}';
  if (/tfsa/.test(n)) return '\u{1F337}';
  if (/fhsa|home|house/.test(n)) return '\u{1F3E1}';
  if (/dcpp|dbpp|pension|rpp|group/.test(n)) return '\u{1F3E2}';
  if (/resp|educ|school/.test(n)) return '\u{1F393}';
  if (/crypto|bitcoin|btc|eth/.test(n)) return '\u{1FA99}';
  return '\u{1F4C8}';
};

const INVEST_EMOJIS = ['\u{1F4C8}', '\u{1F334}', '\u{1F337}', '\u{1F3E1}', '\u{1F3E2}', '\u{1F393}', '\u{1F331}', '\u{1FAB4}', '\u{1F48E}', '\u{1F3E6}', '⭐', '\u{1FA99}', '\u{1F33B}', '\u{1F4B0}', '\u{1F30D}', '\u{1F680}'];

function EmojiSheet({ accounts, onPick, onClose }) {
  const [open, setOpen] = useState(null);
  const [typed, setTyped] = useState('');
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 5000 }} data-patch="plan_22">
      <div onClick={onClose} style={{ position: 'absolute', inset: '-60px 0', background: 'rgba(35,38,43,0.32)' }} />
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, maxHeight: '82vh', overflowY: 'auto', background: '#FFFFFF', borderRadius: '28px 28px 0 0', padding: '18px 18px calc(env(safe-area-inset-bottom) + 24px)', boxShadow: '0 -8px 30px rgba(40,30,80,0.08)' }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
          <span style={{ fontSize: 17, fontWeight: 600, color: '#26252E' }}>Investment emojis</span>
          <button type="button" onClick={onClose} style={{ border: 0, background: 'none', padding: 0, fontSize: 15, fontWeight: 600, color: '#7A5DD3' }}>Done</button>
        </div>
        {accounts.map((a, i) => {
          const isOpen = open === a.id;
          const cur = investEmoji(a);
          return (
            <div key={a.id} style={{ borderTop: i ? '1px solid #F1EEF8' : 0 }}>
              <button type="button" onClick={() => { setOpen(isOpen ? null : a.id); setTyped(''); }} className="flex items-center w-full" style={{ gap: 12, padding: '10px 0', border: 0, background: 'none', textAlign: 'left' }}>
                <span className="flex items-center justify-center flex-shrink-0" style={{ width: 42, height: 42, borderRadius: 14, background: '#EEEAFB', fontSize: 20 }}>{cur}</span>
                <span className="truncate" style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 500, color: '#26252E' }}>{a.name}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#7A5DD3' }}>{isOpen ? 'Close' : 'Change'}</span>
              </button>
              {isOpen && (
                <div style={{ padding: '2px 0 12px 54px' }}>
                  <div className="flex flex-wrap" style={{ gap: 6 }}>
                    {INVEST_EMOJIS.map((e) => (
                      <button key={e} type="button" onClick={() => onPick(a.id, e)} style={{ width: 38, height: 38, borderRadius: 12, border: 0, padding: 0, fontSize: 18, background: cur === e ? '#ECE6FA' : '#F4F1FB', boxShadow: cur === e ? 'inset 0 0 0 2px #B9A6F0' : 'none' }}>{e}</button>
                    ))}
                  </div>
                  <div className="flex items-center" style={{ gap: 8, marginTop: 10 }}>
                    <input
                      value={typed}
                      onChange={(ev) => setTyped(ev.target.value.slice(0, 8))}
                      placeholder="Or type any emoji"
                      style={{ flex: 1, minWidth: 0, height: 40, borderRadius: 12, border: 0, background: '#F4F1FB', padding: '0 12px', fontSize: 16, color: '#26252E', outline: 'none' }}
                    />
                    <button type="button" disabled={!typed.trim()} onClick={() => { onPick(a.id, typed.trim()); setTyped(''); }} style={{ height: 40, borderRadius: 999, border: 0, padding: '0 16px', background: '#1F1E24', color: '#FFFFFF', fontSize: 14, fontWeight: 600, opacity: typed.trim() ? 1 : 0.4 }}>Use</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>,
    document.body
  );
}

// items: [{ id, name, emoji, balance }]; onSaveEmoji(id, emoji)
export const InvestPath = ({ items = [], onSaveEmoji }) => {
  const [editing, setEditing] = useState(false);
  if (!items.length) return null;
  const steps = items.slice(0, 5).map((a, i) => {
    const t = a.tint || planFundTint(i); // dark_05: the account's own colour
    return { emoji: investEmoji(a), label: shortName(a.name), amount: pathMoney(a.balance, items.length), amountBg: t.wash, amountInk: t.ink };
  });
  return (
    <>
      <PlanPath steps={steps} flow />
      {onSaveEmoji && (
        <div style={{ textAlign: 'center', marginTop: 10 }}>
          <button type="button" onClick={() => setEditing(true)} style={{ border: 0, background: 'none', padding: 0, fontSize: 13, fontWeight: 600, color: '#8B879A' }}>✎ Edit emojis</button>
        </div>
      )}
      {editing && <EmojiSheet accounts={items} onPick={onSaveEmoji} onClose={() => setEditing(false)} />}
    </>
  );
};
